import axios from 'axios'
import { computed, ComputedRef, reactive } from 'vue'

import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";

import TopLanguageStats from '../DTOs/TopLanguagesStats'
import { FetchForAliasArgs, FetchForTagArgs, FetchForTeamArgs } from './types'

/**
 * Represents the resonse from the server. Eventually this type will be auto generated
 * by the backend but for now its here
 */
export type TopLanguageResponseItem = {
  language: string
  hits: number
}
export type TopLanguageResponseData = {
  dataset: TopLanguageResponseItem[]
  none: number
  total: number
}

export type iTopLanguageStatsAPI = {
  dataset: ComputedRef<Readonly<TopLanguageStats>>
  loading: ComputedRef<boolean>
  setRouter: (router: HttpRouter<'get'>) => iTopLanguageStatsAPI
  fetchForTeam: (args: FetchForTeamArgs) => Promise<void>
  fetchForAlias: (args: FetchForAliasArgs) => Promise<void>
  fetchForTag: (args: FetchForTagArgs) => Promise<void>
}

/**
 * Returns data that is a valid Popularity Stat
 */
export function DefaultTopLanguageStats(): TopLanguageResponseItem[] {
  let retval: TopLanguageResponseItem[] = []
  const languages = ['English', 'French', 'Chinese', 'German']
  let i = 1
  Object.values(languages).forEach((language: string) => {
    retval.push({ language, hits: i * 11 })
    i++
  })

  return retval
}

/**
 * A tool to allow you to fetch and interact with "popular times" analytics
 * data fetched from the server.
 */
export default function TopLanguageStatsAPI(
  debug = false
): iTopLanguageStatsAPI {
  const state = reactive<{
    dataset: TopLanguageStats
    loading: boolean
  }>({
    dataset: new TopLanguageStats(),
    loading: false,
  })

  let router: HttpRouter<'get'> = axios

  async function _fetch(url: string, from: string, to: string): Promise<void> {
    state.loading = true

    if (debug) {
      // Transform language -> source to match ValueChartData's expected format
      const debugDataset = DefaultTopLanguageStats().map(item => ({
        source: item.language,
        hits: item.hits,
      }))
      state.dataset = new TopLanguageStats(
        debugDataset,
        11,
        121,
        from,
        to
      )
    } else {
      const response: HttpResponse<TopLanguageResponseData> =
        await router.get(url)

      if (response.status !== 200) {
        throw new Error('Could not fetch top language stats data from ' + url)
      }

      const theData = response.data
      // Transform language -> source to match ValueChartData's expected format
      const transformedDataset = theData.dataset.map(item => ({
        source: item.language,
        hits: item.hits,
      }))
      state.dataset = new TopLanguageStats(
        transformedDataset,
        theData.none,
        theData.total
      )
    }

    state.loading = false
  }

  return {
    loading: computed(() => state.loading),
    dataset: computed(() => state.dataset),

    fetchForTeam: async function ({
      orgID,
      teamID = null,
      from,
      to,
      archived = false,
    }): Promise<void> {
      const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/languages/top?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`
      return _fetch(url, from, to)
    },

    fetchForAlias: async function ({
      orgID,
      teamID = null,
      alias,
      from,
      to,
      archived = false,
    }): Promise<void> {
      const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/languages/top?alias[]=${encodeURIComponent(alias)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`
      return _fetch(url, from, to)
    },

    fetchForTag: async function ({
      orgID,
      teamID = null,
      tag,
      from,
      to,
      archived = false,
    }): Promise<void> {
      const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/languages/top?tag[]=${encodeURIComponent(tag)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`
      return _fetch(url, from, to)
    },

    /**
     * Allows you to swap out the router for a fake one when writing tests
     */
    setRouter(newRouter) {
      router = newRouter
      return this
    },
  }
}
